/**
 * Recent Packaging Table Component - Display last 3 days of packaging data
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Clock } from 'lucide-react';
import { usePackagingTableData } from '@/hooks/useProductionData';
import { ProductionFilters } from '@/types/production';

interface RecentPackagingTableProps {
  filters: ProductionFilters;
}

export const RecentPackagingTable: React.FC<RecentPackagingTableProps> = ({ filters }) => {
  // Fetch packaging table data
  const {
    data: packagingData = [],
    isLoading,
    error
  } = usePackagingTableData(filters);

  // Get last 3 days sorted by date (newest first)
  const recentData = React.useMemo(() => {
    return [...packagingData]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [packagingData]);

  // Format number with decimal places
  const formatNumber = (value: number | null, decimals: number = 2) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-red-600">
            <p>Lỗi tải dữ liệu: {error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-green-600" />
          📦 Ba ngày gần nhất
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recentData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center font-medium w-[120px] border-r">Ngày</TableHead>
                  
                  {/* Công lao động */}
                  <TableHead className="text-center font-medium bg-orange-50 border-r">Công lao động</TableHead>
                  
                  {/* M³ Wood */}
                  <TableHead className="text-center font-medium bg-blue-50 border-r">M³ Wood</TableHead>
                  
                  {/* M³ Thùng */}
                  <TableHead className="text-center font-medium bg-green-50 border-r">M³ Thùng</TableHead>
                  
                  {/* Năng suất Wood */}
                  <TableHead className="text-center font-medium bg-purple-50 border-r">NS Wood</TableHead>
                  
                  {/* Năng suất Thùng */}
                  <TableHead className="text-center font-medium bg-indigo-50 border-r">NS Thùng</TableHead>
                  
                  {/* Số Container */}
                  <TableHead className="text-center font-medium bg-yellow-50">Số Container</TableHead>
                </TableRow>
                <TableRow>
                  <TableHead className="border-r"></TableHead>
                  
                  {/* Sub-headers with units */}
                  <TableHead className="text-xs text-center border-r">Công</TableHead>
                  <TableHead className="text-xs text-center border-r">M³</TableHead>
                  <TableHead className="text-xs text-center border-r">M³</TableHead>
                  <TableHead className="text-xs text-center border-r">M³/Công</TableHead>
                  <TableHead className="text-xs text-center border-r">M³/Công</TableHead>
                  <TableHead className="text-xs text-center">Container</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentData.map((item, index) => (
                  <TableRow key={index} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-center border-r">
                      {new Date(item.date).toLocaleDateString('vi-VN')}
                    </TableCell>
                    
                    {/* Công lao động */}
                    <TableCell className="text-sm text-center bg-orange-50/30 border-r">
                      {formatNumber(item.labor, 1)}
                    </TableCell>
                    
                    {/* M³ Wood */}
                    <TableCell className="text-sm text-center bg-blue-50/30 border-r">
                      {formatNumber(item.volumeWood, 2)}
                    </TableCell>
                    
                    {/* M³ Thùng */}
                    <TableCell className="text-sm text-center font-medium text-green-700 bg-green-50/30 border-r">
                      {formatNumber(item.volumeCarton, 2)}
                    </TableCell>
                    
                    {/* Năng suất Wood */}
                    <TableCell className="text-sm text-center bg-purple-50/30 border-r">
                      {formatNumber(item.productivityWood, 3)}
                    </TableCell>
                    
                    {/* Năng suất Thùng */}
                    <TableCell className="text-sm text-center font-medium text-indigo-700 bg-indigo-50/30 border-r">
                      {formatNumber(item.productivityCarton, 3)}
                    </TableCell>
                    
                    {/* Số Container */}
                    <TableCell className="text-sm text-center font-medium text-yellow-700 bg-yellow-50/30">
                      {formatNumber(item.containerCount, 1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Không có dữ liệu thành phẩm đóng gói</p>
            <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra kết nối dữ liệu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};